import styled from '@emotion/styled';
import { Button, Card, Loader, MantineTheme, Modal, Space, Text, useMantineTheme } from '@mantine/core';
import { useDi } from '@root/Services/DI';
import { DataFileReader } from '@root/Services/Query/DataFileReader';
import { useCallback, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

interface LineItemFileUploaderProps {
    opened: boolean;
    onClose: () => void;
    onImport: (csvData: Record<string, unknown>[]) => void;
}

export function LineItemFileUploader(props: LineItemFileUploaderProps) {
    const fileReader = useDi(DataFileReader);
    const [uploadState, setUploadState] = useState<'uploading' | 'imported' | 'error' | undefined>(undefined);
    const theme = useMantineTheme();

    const onDrop = useCallback(
        async (acceptedFile: File[], fileRejections: FileRejection[]) => {
            var file = acceptedFile[0];
            if (file.type !== 'text/csv' && file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
                setUploadState('error');
            } else {
                setUploadState('uploading');
                if (file !== null && file !== undefined) {
                    try {
                        const { headers, data, error } = await fileReader.readFlatFile(file);

                        if (!data || !headers) {
                            setUploadState('error');
                            fileRejections.push({ file, errors: [{ code: 'no_data', message: 'No data found in file' }] });
                            return { error: error ?? 'No data found in file' };
                        }
                        if (error) {
                            setUploadState('error');
                            fileRejections.push({ file, errors: [{ code: 'parse_error', message: error }] });
                            return { error };
                        }
                        if (headers.length <= 0) {
                            setUploadState('error');
                            fileRejections.push({
                                file,
                                errors: [
                                    {
                                        code: 'no_headers',
                                        message: 'File must have headers that match focus field names',
                                    },
                                ],
                            });
                            return { error: 'File must have headers' };
                        }

                        // Create an array of field maps, one for each data row
                        const csvFieldMaps: Record<string, unknown>[] = [];

                        // Process each data row
                        data.forEach((row, rowIndex) => {
                            const csvFieldMap: Record<string, unknown> = {};
                            headers.forEach((header, colIndex) => {
                                const fieldName = header.trim().replace(/\s/g, '');
                                const value = row[colIndex];
                                if (value) {
                                    csvFieldMap[fieldName] = typeof value === 'string' ? value.trim() : value;
                                }
                            });
                            if (Object.keys(csvFieldMap).length > 0) {
                                csvFieldMaps.push(csvFieldMap);
                            }
                        });

                        // Automatically import the fields
                        props.onImport(csvFieldMaps);
                        setUploadState('imported');
                    } catch (error) {
                        setUploadState('error');
                    }
                }
            }
        },
        [fileReader]
    );

    const { getRootProps, getInputProps, isDragActive, isFocused, isDragAccept, isDragReject, fileRejections } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        },
        maxFiles: 1,
    });

    const handleImportMore = () => {
        setUploadState(undefined);
    };

    const handleCancel = () => {
        props.onClose();
        setUploadState(undefined);
    };

    return (
        <Modal opened={props.opened} onClose={handleCancel} title="Import Field Values from CSV" size="md" centered>
            <Card>
                <Text weight="bolder" align="center">
                    Upload CSV file with field values to import
                </Text>
                <Space h="sm" />

                <FileContainer {...getRootProps({ isFocused, isDragAccept, isDragReject, isDragActive, uploadState })}>
                    <input {...getInputProps()} />
                    {uploadState === 'imported' ? (
                        <>
                            <img
                                src="/assets/cloud-upload.svg"
                                style={{
                                    width: '75px',
                                    filter: '',
                                }}
                            />
                            <Space h="md" />
                            <p>Upload Success</p>
                            <Space h="md" />
                            <Button sx={{ margin: '0px auto', display: 'flex' }} onClick={handleImportMore}>
                                Upload Another File?
                            </Button>
                        </>
                    ) : uploadState === 'uploading' ? (
                        <>
                            <div style={{ marginTop: '20px', textAlign: 'center', position: 'relative' }}>
                                <Loader />
                            </div>
                            <Space h="lg" />
                            <p>Reading CSV Data</p>
                            <Space h="md" />
                            <p>Please Wait...</p>
                        </>
                    ) : uploadState === 'error' ? (
                        <>
                            <img src="/assets/cloud-upload-not.svg" style={{ width: '75px' }} />
                            <Space h="md" />
                            <p>{fileRejections.flatMap((e) => e.errors.map((f) => f.message)).join(', ')}</p>
                            <Space h="md" />
                            <p>
                                <a
                                    style={{
                                        margin: '0px auto',
                                        color: theme.colors.error[7],
                                        textDecoration: 'underline',
                                        textAlign: 'center',
                                        position: 'relative',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setUploadState(undefined)}
                                >
                                    Try Again
                                </a>
                            </p>
                        </>
                    ) : (
                        <>
                            {isDragReject ? (
                                <img src="/assets/cloud-upload-not.svg" style={{ width: '75px' }} />
                            ) : (
                                <img
                                    src="/assets/cloud-upload.svg"
                                    style={{
                                        width: '75px',
                                        filter: '',
                                    }}
                                />
                            )}
                            <span>[CSV, XLSX]</span>
                            <p>Drag & Drop</p>
                            <p>Field Values CSV File Here</p>
                            <p>
                                or <FakeLink onClick={(e) => e.preventDefault()}>Browse</FakeLink>
                            </p>
                        </>
                    )}
                </FileContainer>

                <Space h="md" />
            </Card>
        </Modal>
    );
}

const FileContainer = styled.div`
    width: 90%;
    margin: 0px auto;
    border: 2px dashed ${(p) => getColor(p)};
    height: 200px;
    background-color: ${(p) => getBackgroundColor(p)};

    span {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 14px;
        font-weight: 800;
        color: ${(p) => getLinkTextColor(p, p.theme)};
    }

    img {
        margin: 10px auto;
        display: flex;
        filter: ${(p) => getSvgFilter(p)};
    }

    p {
        text-align: center;
        font-size: 14px;
        line-height: 0.25;
        font-weight: 800;
    }
`;

const FakeLink = styled.p`
    color: ${(p) => getLinkTextColor(p, p.theme)};
    text-decoration: underline;

    :hover {
        color: ${(p) => p.theme.colors.primary[8]};
        cursor: pointer;
    }
`;

const getLinkTextColor = (props: any, theme: MantineTheme) => {
    if (props.uploadState === 'imported') {
        return '#fff';
    }

    if (props.isDragReject || props.uploadState === 'error') {
        return theme.colors.error[7];
    }

    return theme.colors.primary[6];
};

const getColor = (props: any) => {
    const theme = useMantineTheme();

    if (props.isDragReject || props.uploadState === 'error') {
        return theme.colors.error[5];
    }
    if (props.isFocused || props.isDragActive || props.uploadState === 'imported') {
        return theme.colors.primary[5];
    }
    return theme.colors.gray[4];
};

const getBackgroundColor = (props: any) => {
    const theme = useMantineTheme();
    if (props.isDragReject || props.uploadState === 'error') {
        return theme.colors.error[2];
    }
    if (props.isFocused || props.isDragActive || props.uploadState === 'imported') {
        return theme.colors.primary[2];
    }
    return '#fff';
};

const getSvgFilter = (props: any) => {
    if (props.isDragReject || props.uploadState === 'error') {
        return 'invert(21%) sepia(44%) saturate(6355%) hue-rotate(354deg) brightness(73%) contrast(90%)';
    }

    return 'invert(74%) sepia(48%) saturate(6723%) hue-rotate(162deg) brightness(86%) contrast(107%)';
};
